import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Paper,
  Stack,
  Group,
  Text,
  Title,
  Button,
  ActionIcon,
  Progress,
  List,
  Alert,
  Badge,
  ThemeIcon,
  ScrollArea,
} from '@mantine/core';
import {
  IconX,
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconAlertTriangle,
  IconCircleOff,
  IconRoute,
  IconMinus,
  IconChevronUp,
  IconRocket,
} from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import type { TourStep } from '../../onboarding/tourSteps';

/**
 * Abstand von unten, damit der KIAN-Knopf die Karte nicht überdeckt.
 *
 * Der FAB sitzt fest auf `bottom: 1.5rem` mit 56 px Höhe und liegt auf z-Index 5000 —
 * also über dem Rundgang. Ihn zu unterlaufen ist keine Option: der Chat muss während
 * des Rundgangs erreichbar bleiben. Also weicht die Karte nach oben aus.
 * Die Werte sind bewusst mit `KianFAB.tsx` gespiegelt; ändert sich dort die Größe,
 * muss es hier mitwandern.
 */
const FAB_HEIGHT_PX = 56;
const FAB_BOTTOM_REM = 1.5;
const GAP_PX = 12;
/** Synchron mit `components/layout/BottomNav.module.css` → `.bottomNav { height: … }` */
const BOTTOM_NAV_HEIGHT_PX = 70;

export interface GuidedTourShellProps {
  /** Bereits rollengefilterte Stationen. */
  steps: TourStep[];
  /** Event-Name, auf den der Rundgang zum Starten hört. */
  openEvent: string;
  /** localStorage-Schlüssel für „schon gesehen". */
  storageKey: string;
  /** Zusätzlich beim Beenden ausführen — z. B. Abschluss serverseitig merken. */
  onFinish?: () => void;
}

/**
 * Geführter Rundgang — gemeinsames Gerüst für Techniker- und Kundenportal.
 *
 * Läuft auf der echten Oberfläche mit den echten Daten des Nutzers, statt auf
 * Screenshots: die Oberfläche ändert sich hier wöchentlich, ein Bildschirmfoto wäre
 * nach dem nächsten Release falsch — und niemand merkt es. Der Rundgang navigiert
 * zur jeweiligen Seite und legt die Erklärung als Karte daneben; die Seite bleibt
 * dabei bedienbar, damit man das Erklärte gleich ausprobieren kann.
 *
 * Die Stationen kommen von außen, damit beide Portale dasselbe Verhalten teilen,
 * ohne voneinander zu wissen — das Kundenportal hat weder `useAuthStore` noch
 * dieselben Rollen.
 */
export function GuidedTourShell({ steps, openEvent, storageKey, onFinish }: GuidedTourShellProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [minimized, setMinimized] = useState(false);

  // Auf schmalen Bildschirmen rutscht der FAB über die BottomNav — die Karte mit.
  const bottomNavVisible = useMediaQuery('(max-width: 47.99em)');
  const bottomOffset = `calc(${FAB_BOTTOM_REM}rem + ${FAB_HEIGHT_PX + GAP_PX}px${
    bottomNavVisible ? ` + ${BOTTOM_NAV_HEIGHT_PX}px` : ''
  } + env(safe-area-inset-bottom, 0px))`;

  useEffect(() => {
    const handler = () => {
      setIndex(0);
      setMinimized(false);
      setOpen(true);
    };
    window.addEventListener(openEvent, handler);
    return () => window.removeEventListener(openEvent, handler);
  }, [openEvent]);

  const step = steps[index];

  // Zur Seite des Schritts wechseln — aber nur, wenn man nicht ohnehin dort ist.
  // Sonst würde jeder Klick auf der Seite selbst zurückgesetzt.
  useEffect(() => {
    if (!open || !step) return;
    const target = step.path;
    const current = `${location.pathname}${location.search}`;
    if (current !== target) navigate(target);
    // location bewusst NICHT in den Abhängigkeiten: der Nutzer soll sich während
    // eines Schrittes frei bewegen dürfen, ohne zurückgezogen zu werden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, step?.path]);

  if (!open || !step) return null;

  const isLast = index === steps.length - 1;

  const finish = () => {
    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      // Ohne localStorage läuft der Rundgang trotzdem — er merkt sich nur nichts.
    }
    onFinish?.();
    setOpen(false);
  };

  if (minimized) {
    return (
      <Paper
        withBorder
        shadow="md"
        radius="md"
        p="xs"
        style={{ position: 'fixed', right: 16, bottom: bottomOffset, zIndex: 3000 }}
      >
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size="sm" variant="light" color="orange">
            <IconRoute size={14} />
          </ThemeIcon>
          <Text size="xs">
            Rundgang · Schritt {index + 1}/{steps.length}
          </Text>
          <ActionIcon size="sm" variant="subtle" onClick={() => setMinimized(false)}>
            <IconChevronUp size={14} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" color="gray" onClick={finish}>
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Paper>
    );
  }

  return (
    <Paper
      withBorder
      shadow="lg"
      radius="md"
      style={{
        position: 'fixed',
        right: 16,
        bottom: bottomOffset,
        width: 'min(420px, calc(100vw - 32px))',
        // Höhe um den Freiraum unter der Karte kürzen, sonst wächst sie oben aus dem Bild.
        maxHeight: `min(calc(100vh - ${FAB_HEIGHT_PX + GAP_PX + 48}px), 640px)`,
        zIndex: 3000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Group justify="space-between" p="sm" pb={4} wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size="sm" variant="light" color="orange">
            <IconRoute size={14} />
          </ThemeIcon>
          <Text size="xs" c="dimmed">
            Rundgang · Schritt {index + 1} von {steps.length}
          </Text>
          {step.adminOnly && (
            <Badge size="xs" color="grape" variant="light">
              Admin
            </Badge>
          )}
          {step.comingSoon && (
            <Badge size="xs" color="violet" variant="light">
              Demnächst
            </Badge>
          )}
        </Group>
        <Group gap={4} wrap="nowrap">
          <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => setMinimized(true)} title="Einklappen">
            <IconMinus size={14} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" color="gray" onClick={finish} title="Rundgang beenden">
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Group>

      <Progress value={((index + 1) / steps.length) * 100} size="xs" color="orange" radius={0} />

      <ScrollArea style={{ flex: 1 }} p="sm">
        <Stack gap="sm">
          <Title order={4}>{step.title}</Title>
          {step.comingSoon && (
            <Alert color="violet" variant="light" icon={<IconRocket size={16} />} p="xs">
              <Text size="xs">
                Dieser Bereich ist noch nicht freigeschaltet. Er steht schon im Menü,
                zeigt aber bis zur Freischaltung keine Daten.
              </Text>
            </Alert>
          )}
          <Text size="sm">{step.body}</Text>

          {step.steps && step.steps.length > 0 && (
            <List size="sm" spacing={4} icon={
              <ThemeIcon size={16} radius="xl" variant="light" color="orange">
                <IconCheck size={10} />
              </ThemeIcon>
            }>
              {step.steps.map((s) => (
                <List.Item key={s}>{s}</List.Item>
              ))}
            </List>
          )}

          {step.caution && (
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
              <Text size="xs">{step.caution}</Text>
            </Alert>
          )}

          {step.notFor && (
            <Alert color="gray" variant="light" icon={<IconCircleOff size={16} />} p="xs">
              <Text size="xs">
                <Text span fw={600}>Brauchst du nicht, wenn: </Text>
                {step.notFor}
              </Text>
            </Alert>
          )}
        </Stack>
      </ScrollArea>

      <Group justify="space-between" p="sm" pt={4} wrap="nowrap">
        <Button
          size="xs"
          variant="default"
          leftSection={<IconArrowLeft size={14} />}
          disabled={index === 0}
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
        >
          Zurück
        </Button>
        <Group gap="xs" wrap="nowrap">
          {!isLast && (
            <Button size="xs" variant="subtle" color="gray" onClick={finish}>
              Überspringen
            </Button>
          )}
          <Button
            size="xs"
            rightSection={isLast ? <IconCheck size={14} /> : <IconArrowRight size={14} />}
            onClick={() => (isLast ? finish() : setIndex((i) => i + 1))}
          >
            {isLast ? 'Fertig' : 'Weiter'}
          </Button>
        </Group>
      </Group>
    </Paper>
  );
}
